import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useColorContext } from '../../context/colorContext';
import { useLanguageContext } from '../../context/languageContext';
import { useNoteDispatch, useNoteState } from '../../context/noteContexts/noteContext';
import { useUserLimitDispatch, useUserLimitState } from '../../context/user_limit_context';
import VisualNote from './VarkViews/VisualNote';
import AudioNote from './VarkViews/AudioNote';
import ReadView from './VarkViews/ReadView';
import KinesthicView from './VarkViews/kinesthicView';
import AIChatFloatingButton from '../../components/features/AIchat/AIchatElements';
import AIChat from '../../components/features/AIchat/AIchat';
import {
	appendUserMessageToConversationJson,
	sendChatConversationToBackend,
} from '../../utils/chatConversation';
import { extractPlainTextFromTipTap } from '../../utils/tiptapPlainText';

const hasNonEmptyArray = (value) => Array.isArray(value) && value.length > 0;

const toSafeHex = (hex, fallback) => {
	const value = String(hex || '').trim();
	return /^#([A-Fa-f0-9]{6})$/.test(value) ? value : fallback;
};

const hexToRgba = (hex, alpha, fallback = '#000000') => {
	const safeHex = toSafeHex(hex, fallback);
	const r = parseInt(safeHex.slice(1, 3), 16);
	const g = parseInt(safeHex.slice(3, 5), 16);
	const b = parseInt(safeHex.slice(5, 7), 16);
	return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

function NoteAdaptedView() {
	const {
		name_note,
		note_id,
		EditorContent_json_tiptap,
		if_loading_note_content,
		note_adapting_run_keys,
		if_note_summarized_or_deep,
		current_note_type,
		visualMindMapTitles,
		visualMindMapsJson,
		visualMindMapTitlesSummarized,
		visualMindMapsJsonSummarized,
		audioBlockTitles,
		audioBlockJson,
		audioBlockTitlesSummarized,
		audioBlockJsonSummarized,
		list_content_smart_text,
		list_title_smart_text,
		list_content_smart_textSummarized,
		list_title_smart_textSummarized,
		listJson_content_flashCards,
		listJson_content_flashCardsSummarized,
		chat_conversation_json,
	} = useNoteState();
	const {
		AdaptNote,
		setIf_note_summarized_or_deep,
		setChat_conversation_json,
		setName_note,
		flushCurrentNoteToDatabase,
	} = useNoteDispatch();
	const { incrementChatConversation, openUpgradePopup } = useUserLimitDispatch();
	const { isChatLimitReached, isAdaptionLimitReached, adaptionLimit } = useUserLimitState();
	const { t } = useLanguageContext();
	const {
		bgPrimary,
		bgSecondary,
		textPrimary,
		textSecondary,
		activeAccent,
	} = useColorContext();

	const safeBgPrimary = toSafeHex(bgPrimary, '#0D1117');
	const safeBgSecondary = toSafeHex(bgSecondary, '#151B23');
	const safeTextPrimary = toSafeHex(textPrimary, '#E6EDF3');
	const safeTextSecondary = toSafeHex(textSecondary, '#9BA7B4');
	const safeAccent = toSafeHex(activeAccent, '#2EA043');
	const signInGradient = `linear-gradient(270deg, ${safeAccent}, #a855f7, #000000, #a855f7, ${safeAccent})`;
	const signInBaseButtonStyle = {
		background: signInGradient,
		backgroundSize: '300% 300%',
		border: 'none',
		borderRadius: 12,
		color: '#FFFFFF',
		boxShadow: `0 12px 28px ${hexToRgba(safeAccent, 0.22, safeAccent)}`,
	};
	const signInHoverButtonStyle = {
		filter: 'brightness(1.08) saturate(1.05)',
		boxShadow: `0 16px 34px ${hexToRgba(safeAccent, 0.35, safeAccent)}`,
	};
	const signInActiveButtonStyle = {
		filter: 'none',
		boxShadow: `0 12px 28px ${hexToRgba(safeAccent, 0.22, safeAccent)}`,
	};
	const sideMenuLabelTextStyle = {
		fontSize: 14,
		fontWeight: 500,
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
		overflow: 'hidden',
		transition: 'opacity 0.28s ease, transform 0.28s ease, max-width 0.28s ease',
		willChange: 'opacity, transform, max-width',
	};
	const [isChatOpen, setIsChatOpen] = useState(false);
	const [isChatFullscreen, setIsChatFullscreen] = useState(false);
	const [isChatVisible, setIsChatVisible] = useState(false);
	const [isChatAnimatedIn, setIsChatAnimatedIn] = useState(false);
	const [isChatSubmitting, setIsChatSubmitting] = useState(false);
	const [adaptError, setAdaptError] = useState(null);
	const [isEditingTitle, setIsEditingTitle] = useState(false);
	const [titleDraft, setTitleDraft] = useState('');
	const [titleChangeAnimate, setTitleChangeAnimate] = useState(false);
	const [pendingPersistedTitle, setPendingPersistedTitle] = useState('');
	const [chatPanelWidth, setChatPanelWidth] = useState(430);
	const [chatViewportWidth, setChatViewportWidth] = useState(() => (typeof window === 'undefined' ? 1280 : window.innerWidth));
	const [windowWidth, setWindowWidth] = useState(() => (typeof window === 'undefined' ? 1280 : window.innerWidth));
	const [windowHeight, setWindowHeight] = useState(() => (typeof window === 'undefined' ? 900 : window.innerHeight));
	const isChatResizingRef = useRef(false);
	const chatAnimationFrameRef = useRef(null);
	const titleInputRef = useRef(null);
	const CHAT_PANEL_MIN_WIDTH = 320;
	const CHAT_PANEL_MAX_WIDTH = 760;
	const CHAT_PANEL_RIGHT_OFFSET = 14;
	const CHAT_PANEL_SCREEN_MARGIN = 28;

	const pageBackground = useMemo(() => safeBgPrimary, [safeBgPrimary]);

	const selectedMode = String(if_note_summarized_or_deep || 'deep').toLowerCase() === 'summarized'
		? 'summarized'
		: 'deep';
	const normalizedNoteType = String(current_note_type || 'visual').toLowerCase();
	const currentFormatRunKey = `adapted|${normalizedNoteType}|${selectedMode}`;
	const isAdaptLoading = Array.isArray(note_adapting_run_keys)
		&& note_adapting_run_keys.includes(currentFormatRunKey);
	const isAudioMode = normalizedNoteType === 'audio';
	const isKinestheticMode = normalizedNoteType === 'kinesthic';
	const hasEditorContent = !!EditorContent_json_tiptap && typeof EditorContent_json_tiptap === 'object';
	const isCurrentFormatGenerated = useMemo(() => {
		if (normalizedNoteType === 'read') {
			return selectedMode === 'summarized'
				? hasNonEmptyArray(list_content_smart_textSummarized) || hasNonEmptyArray(list_title_smart_textSummarized)
				: hasNonEmptyArray(list_content_smart_text) || hasNonEmptyArray(list_title_smart_text);
		}

		if (normalizedNoteType === 'kinesthic') {
			return selectedMode === 'summarized'
				? hasNonEmptyArray(listJson_content_flashCardsSummarized)
				: hasNonEmptyArray(listJson_content_flashCards);
		}

		if (normalizedNoteType === 'audio') {
			return selectedMode === 'summarized'
				? hasNonEmptyArray(audioBlockTitlesSummarized) || hasNonEmptyArray(audioBlockJsonSummarized)
				: hasNonEmptyArray(audioBlockTitles) || hasNonEmptyArray(audioBlockJson);
		}

		return selectedMode === 'summarized'
			? hasNonEmptyArray(visualMindMapTitlesSummarized) || hasNonEmptyArray(visualMindMapsJsonSummarized)
			: hasNonEmptyArray(visualMindMapTitles) || hasNonEmptyArray(visualMindMapsJson);
	}, [
		normalizedNoteType,
		selectedMode,
		list_content_smart_text,
		list_title_smart_text,
		list_content_smart_textSummarized,
		list_title_smart_textSummarized,
		listJson_content_flashCards,
		listJson_content_flashCardsSummarized,
		audioBlockTitles,
		audioBlockJson,
		audioBlockTitlesSummarized,
		audioBlockJsonSummarized,
		visualMindMapTitles,
		visualMindMapsJson,
		visualMindMapTitlesSummarized,
		visualMindMapsJsonSummarized,
	]);
	const selectedVisualTitles = selectedMode === 'summarized'
		? visualMindMapTitlesSummarized
		: visualMindMapTitles;
	const selectedVisualMaps = selectedMode === 'summarized'
		? visualMindMapsJsonSummarized
		: visualMindMapsJson;
	const selectedAudioTitles = selectedMode === 'summarized'
		? audioBlockTitlesSummarized
		: audioBlockTitles;
	const selectedAudioBlocks = selectedMode === 'summarized'
		? audioBlockJsonSummarized
		: audioBlockJson;
	const selectedSmartTextContents = selectedMode === 'summarized'
		? list_content_smart_textSummarized
		: list_content_smart_text;
	const selectedSmartTextTitles = selectedMode === 'summarized'
		? list_title_smart_textSummarized
		: list_title_smart_text;
	const selectedFlashcards = selectedMode === 'summarized'
		? listJson_content_flashCardsSummarized
		: listJson_content_flashCards;
	const displayedNoteName = String(name_note || '').trim() || 'Note sans titre';
	const adaptedViewText = {
		emptyTitle: t('notes.adaptedView.emptyTitle'),
		emptyDescriptionWithContent: t('notes.adaptedView.emptyDescriptionWithContent'),
		emptyDescriptionWithoutContent: t('notes.adaptedView.emptyDescriptionWithoutContent'),
		noContentFallback: t('notes.adaptedView.noContentFallback'),
		dismissErrorLabel: t('notes.adaptedView.dismissErrorLabel'),
		generateButton: t('notes.adaptedView.generateButton'),
		regenerateButton: t('notes.adaptedView.regenerateButton'),
		deepMode: t('notes.adaptedView.deepMode'),
		summarizedMode: t('notes.adaptedView.summarizedMode'),
	};

	useEffect(() => {
		if (!isEditingTitle) {
			setTitleDraft(displayedNoteName);
		}
	}, [displayedNoteName, isEditingTitle]);

	useEffect(() => {
		if (!pendingPersistedTitle) return;
		if (String(name_note || '').trim() !== pendingPersistedTitle) return;

		let isCancelled = false;
		void (async () => {
			await flushCurrentNoteToDatabase();
		})()
			.catch((error) => {
				if (!isCancelled) {
					console.error('Title persistence failed in NoteAdaptedView:', error);
				}
			})
			.finally(() => {
				if (!isCancelled) {
					setPendingPersistedTitle('');
				}
			});

		return () => {
			isCancelled = true;
		};
	}, [pendingPersistedTitle, name_note, flushCurrentNoteToDatabase]);

	useEffect(() => {
		if (!isEditingTitle) return;
		const rafId = window.requestAnimationFrame(() => {
			titleInputRef.current?.focus();
			titleInputRef.current?.select();
		});
		return () => window.cancelAnimationFrame(rafId);
	}, [isEditingTitle]);

	const commitTitleEdit = useCallback(() => {
		const nextTitle = String(titleDraft || '').trim();
		if (!nextTitle) {
			setTitleDraft(displayedNoteName);
			setIsEditingTitle(false);
			return;
		}

		if (nextTitle !== String(name_note || '').trim()) {
			setName_note(nextTitle);
			setPendingPersistedTitle(nextTitle);
		}

		setIsEditingTitle(false);
		setTitleChangeAnimate(true);
		window.setTimeout(() => setTitleChangeAnimate(false), 680);
	}, [titleDraft, displayedNoteName, name_note, setName_note]);

	const cancelTitleEdit = useCallback(() => {
		setTitleDraft(displayedNoteName);
		setPendingPersistedTitle('');
		setIsEditingTitle(false);
	}, [displayedNoteName]);

	const startTitleEdit = useCallback(() => {
		setTitleDraft(displayedNoteName);
		setPendingPersistedTitle('');
		setIsEditingTitle(true);
	}, [displayedNoteName]);
	const buildAdaptErrorMessage = useCallback((error) => {
		const msg = String(error?.message || '');
		if (msg.includes('502')) {
			return 'The note is too long or complex to adapt right now. Try with a shorter note, or split it into smaller sections.';
		}
		return `Adaptation failed: ${msg || 'An unexpected error occurred.'}`;
	}, []);

	const handleGenerateCurrentFormat = useCallback(() => {
		if (isAdaptionLimitReached) {
			openUpgradePopup(`You have reached the limit of ${adaptionLimit} adaptations on your current plan. Upgrade to continue.`);
			return;
		}
		if (!hasEditorContent || isAdaptLoading) return;
		setAdaptError(null);
		AdaptNote().catch((error) => {
			console.error('Manual AdaptNote failed:', error);
			setAdaptError(buildAdaptErrorMessage(error));
		});
	}, [isAdaptionLimitReached, openUpgradePopup, AdaptNote, hasEditorContent, isAdaptLoading, buildAdaptErrorMessage]);

	const handleRegenerateCurrentFormat = useCallback(() => {
		if (isAdaptionLimitReached) {
			openUpgradePopup(`You have reached the limit of ${adaptionLimit} adaptations on your current plan. Upgrade to continue.`);
			return;
		}
		if (!hasEditorContent || isAdaptLoading) return;
		setAdaptError(null);
		AdaptNote({ forceRegenerate: true }).catch((error) => {
			console.error('Manual force AdaptNote failed:', error);
			setAdaptError(buildAdaptErrorMessage(error));
		});
	}, [isAdaptionLimitReached, openUpgradePopup, AdaptNote, hasEditorContent, isAdaptLoading, buildAdaptErrorMessage]);

	const handleSelectAdaptMode = useCallback((nextMode) => {
		const normalizedMode = String(nextMode || '').toLowerCase() === 'summarized' ? 'summarized' : 'deep';
		setIf_note_summarized_or_deep(normalizedMode);
	}, [setIf_note_summarized_or_deep]);

	const handleChatSubmit = useCallback(async (messageText) => {
		if (isChatLimitReached) {
			openUpgradePopup("You have reached the free plan limit of 10 conversations. Upgrade to continue.");
			return;
		}
		const nextConversation = appendUserMessageToConversationJson(chat_conversation_json, messageText);
		setChat_conversation_json(nextConversation);
		const sourceText = extractPlainTextFromTipTap(EditorContent_json_tiptap);

		try {
			setIsChatSubmitting(true);
			const updatedConversation = await sendChatConversationToBackend(nextConversation, {
				noteId: note_id,
				sourceText,
				sourceType: 'editor-content',
				sourceFormat: 'tiptap-plain-text',
			});
			if (updatedConversation) {
				setChat_conversation_json(updatedConversation);
				incrementChatConversation();
			}
		} catch (error) {
			console.error('Chat request failed in NoteAdaptedView:', error);
		} finally {
			setIsChatSubmitting(false);
		}
	}, [isChatLimitReached, openUpgradePopup, chat_conversation_json, setChat_conversation_json, EditorContent_json_tiptap, note_id, incrementChatConversation]);

	useEffect(() => {
		let timeoutId = null;
		let animationFrameId = null;
		if (isChatOpen) {
			setIsChatVisible(true);
			setIsChatAnimatedIn(false);
			if (chatAnimationFrameRef.current) {
				window.cancelAnimationFrame(chatAnimationFrameRef.current);
			}
			animationFrameId = window.requestAnimationFrame(() => {
				chatAnimationFrameRef.current = window.requestAnimationFrame(() => {
					setIsChatAnimatedIn(true);
				});
			});
			return undefined;
		}

		setIsChatAnimatedIn(false);
		if (chatAnimationFrameRef.current) {
			window.cancelAnimationFrame(chatAnimationFrameRef.current);
			chatAnimationFrameRef.current = null;
		}
		timeoutId = window.setTimeout(() => {
			setIsChatVisible(false);
		}, 280);

		return () => {
			if (animationFrameId) {
				window.cancelAnimationFrame(animationFrameId);
			}
			if (chatAnimationFrameRef.current) {
				window.cancelAnimationFrame(chatAnimationFrameRef.current);
				chatAnimationFrameRef.current = null;
			}
			if (timeoutId) {
				window.clearTimeout(timeoutId);
			}
		};
	}, [isChatOpen]);

	useEffect(() => {
		const handleResize = () => {
			setChatViewportWidth(window.innerWidth);
		};

		handleResize();
		window.addEventListener('resize', handleResize);

		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	const chatFullscreenWidth = Math.max(
		CHAT_PANEL_MIN_WIDTH,
		chatViewportWidth - CHAT_PANEL_SCREEN_MARGIN,
	);
		const isTinyViewport = windowWidth <= 420;
		const isSmallViewport = windowWidth <= 640;
		const isMediumViewport = windowWidth <= 920;
		const isShortViewport = windowHeight <= 760;
		const headerHeight = isTinyViewport ? 86 : isSmallViewport ? 72 : isMediumViewport ? 56 : 58;
		const headerPaddingX = isTinyViewport ? 10 : isSmallViewport ? 12 : 16;
		const headerPaddingY = isTinyViewport ? 8 : isSmallViewport ? 8 : 10;
		const switchWidth = isTinyViewport ? 88 : isSmallViewport ? 92 : 100;
		const switchHeight = isTinyViewport ? 34 : 40;
		const switchTranslateX = switchWidth;
		const panelPaddingX = isTinyViewport ? 12 : isSmallViewport ? 16 : 26;
		const panelPaddingTop = isTinyViewport ? 14 : isSmallViewport ? 18 : 22;
		const panelPaddingBottom = isTinyViewport ? 24 : isSmallViewport ? 32 : 40;
		const emptyCardWidth = isTinyViewport ? 'min(94vw, 560px)' : isSmallViewport ? 'min(92vw, 680px)' : 'min(94vw, 820px)';
		const emptyCardPadding = isTinyViewport ? '24px 18px 20px' : isSmallViewport ? '28px 24px 24px' : '34px 34px 28px';
		const emptyCardTitleSize = isTinyViewport ? '0.98rem' : isSmallViewport ? '1.02rem' : '1.05rem';
		const emptyCardTextSize = isTinyViewport ? '0.82rem' : isSmallViewport ? '0.86rem' : '0.9rem';
		const actionButtonPadding = isTinyViewport ? '10px 16px' : '12px 20px';
		const actionButtonFontSize = isTinyViewport ? '0.86rem' : '0.93rem';
		const headerErrorMargin = isSmallViewport ? '0 12px 0' : '0 20px 0';
		const headerErrorPadding = isSmallViewport ? '9px 12px' : '10px 16px';
		const contentPadding = isTinyViewport ? '14px 12px 28px' : isSmallViewport ? '18px 16px 32px' : '22px 26px 40px';
	const chatPanelRenderedWidth = isChatFullscreen ? chatFullscreenWidth : chatPanelWidth;
	const chatPanelLeft = isChatFullscreen
		? CHAT_PANEL_SCREEN_MARGIN / 2
		: Math.max(CHAT_PANEL_SCREEN_MARGIN / 2, chatViewportWidth - chatPanelRenderedWidth - CHAT_PANEL_RIGHT_OFFSET);

	const clampChatPanelWidth = useCallback((rawWidth) => {
		const fallbackMax = CHAT_PANEL_MAX_WIDTH;
		const viewportMax = typeof window === 'undefined'
			? fallbackMax
			: Math.max(CHAT_PANEL_MIN_WIDTH, window.innerWidth - CHAT_PANEL_SCREEN_MARGIN);
		const maxAllowed = Math.min(CHAT_PANEL_MAX_WIDTH, viewportMax);
		return Math.max(CHAT_PANEL_MIN_WIDTH, Math.min(maxAllowed, rawWidth));
	}, [CHAT_PANEL_MAX_WIDTH, CHAT_PANEL_MIN_WIDTH, CHAT_PANEL_SCREEN_MARGIN]);

	const handleChatResizeStart = useCallback((event) => {
		event.preventDefault();
		isChatResizingRef.current = true;
	}, []);

	useEffect(() => {
		const handleMouseMove = (event) => {
			if (!isChatResizingRef.current) return;
			const nextWidth = window.innerWidth - event.clientX - CHAT_PANEL_RIGHT_OFFSET;
			setChatPanelWidth(clampChatPanelWidth(nextWidth));
		};

		const handleMouseUp = () => {
			if (!isChatResizingRef.current) return;
			isChatResizingRef.current = false;
		};

		window.addEventListener('mousemove', handleMouseMove);
		window.addEventListener('mouseup', handleMouseUp);

		return () => {
			window.removeEventListener('mousemove', handleMouseMove);
			window.removeEventListener('mouseup', handleMouseUp);
		};
	}, [CHAT_PANEL_RIGHT_OFFSET, clampChatPanelWidth]);

	useEffect(() => {
		const handleWindowResize = () => {
			setChatPanelWidth((previousWidth) => clampChatPanelWidth(previousWidth));
		};

		window.addEventListener('resize', handleWindowResize);
		return () => window.removeEventListener('resize', handleWindowResize);
	}, [clampChatPanelWidth]);

	const isDatabaseLoading = Boolean(if_loading_note_content);
	const loadingTitle = t('notes.loadingTitle');
	const loadingSubtitle = t('notes.loadingSubtitle');

	return (
		<div
			style={{
				height: '100%',
				width: '100%',
				display: 'flex',
				flexDirection: 'column',
				background: pageBackground,
				overflow: 'hidden',
				borderTop: `1.5px solid ${hexToRgba(safeAccent, 0.32, safeAccent)}`,
				borderRight: `1.5px solid ${hexToRgba(safeAccent, 0.32, safeAccent)}`,
				borderBottom: `1.5px solid ${hexToRgba(safeAccent, 0.32, safeAccent)}`,
				borderLeft: 'none',
				boxShadow: 'none',
			}}
		>
			<style>
				{`
										@keyframes ctaGradient {
											0% { background-position: 0% 50%; }
											50% { background-position: 100% 50%; }
											100% { background-position: 0% 50%; }
										}

					@keyframes noteAdaptFadeUp {
						0% { opacity: 0; transform: translateY(18px) scale(0.985); }
						100% { opacity: 1; transform: translateY(0) scale(1); }
					}

					@keyframes noteAdaptSpin {
						from { transform: rotate(0deg); }
						to { transform: rotate(360deg); }
					}

					@keyframes noteAdaptShimmer {
						0% { transform: translateX(-130%); }
						100% { transform: translateX(130%); }
					}

					@keyframes noteTitleBorderPulse {
						0% { opacity: 0; transform: scale(0.985); }
						10% { opacity: 1; transform: scale(1); }
						75% { opacity: 1; transform: scale(1); }
						100% { opacity: 0; transform: scale(1.02); }
					}

					.note-adapted-scroll {
						scrollbar-gutter: stable both-edges;
						scrollbar-width: thin;
						scrollbar-color: ${hexToRgba(safeAccent, 0.62, safeAccent)} transparent;
					}

					.note-adapted-scroll::-webkit-scrollbar {
						width: 14px;
					}

					.note-adapted-scroll::-webkit-scrollbar-track {
						background: transparent;
					}

					.note-adapted-scroll::-webkit-scrollbar-thumb {
						background: ${hexToRgba(safeAccent, 0.62, safeAccent)};
						border-radius: 999px;
						box-shadow: none;
					}

					.note-adapted-scroll::-webkit-scrollbar-thumb:hover {
						background: ${hexToRgba(safeAccent, 0.9, safeAccent)};
						box-shadow: none;
					}
				`}
			</style>

			{isDatabaseLoading || isAdaptLoading ? (
				<div
					style={{
						flex: 1,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						padding: 'clamp(20px, 5vw, 64px) clamp(16px, 4vw, 48px)',
						background: pageBackground,
					}}
				>
					<div
						style={{
							width: 'min(90vw, 640px)',
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							gap: 'clamp(8px, 1.4vh, 12px)',
							textAlign: 'center',
						}}
					>
						<div style={{ fontSize: 'clamp(1.04rem, 1.75vw, 1.42rem)', fontWeight: 700, letterSpacing: '0.008em', color: safeTextPrimary, lineHeight: 1.2 }}>
							{loadingTitle}
						</div>
						<div style={{ fontSize: 'clamp(0.8rem, 1.05vw, 0.92rem)', lineHeight: 1.5, color: safeTextSecondary, maxWidth: 560 }}>
							{loadingSubtitle}
						</div>
						<div
							style={{
								position: 'relative',
								width: 'min(74vw, 460px)',
								height: 'clamp(4px, 0.55vw, 6px)',
								borderRadius: 999,
								overflow: 'hidden',
								background: hexToRgba(safeTextSecondary, 0.18, safeTextSecondary),
							}}
						>
							<div
								style={{
									position: 'absolute',
									inset: 0,
									background: `linear-gradient(90deg, ${hexToRgba(safeAccent, 0.05, safeAccent)} 0%, ${hexToRgba(safeAccent, 0.88, safeAccent)} 48%, ${hexToRgba(safeAccent, 0.05, safeAccent)} 100%)`,
									animation: 'noteAdaptShimmer 1.55s cubic-bezier(0.4, 0, 0.2, 1) infinite',
								}}
							/>
						</div>
					</div>
				</div>
			) : (
				<>
					<div
						style={{
							padding: `${headerPaddingY}px 0 ${isShortViewport ? 2 : 4}px`,
							zIndex: 2,
						}}
					>
						<header
							style={{
								height: headerHeight,
								minHeight: headerHeight,
								display: 'grid',
								gridTemplateColumns: isSmallViewport ? '1fr' : 'minmax(0, 1fr) auto minmax(0, 1fr)',
								gridTemplateRows: isSmallViewport ? 'auto auto' : 'auto',
								alignItems: 'center',
								width: '100%',
								padding: `0 ${headerPaddingX}px`,
								borderRadius: 0,
								border: 'none',
								background: pageBackground,
								boxShadow: 'none',
								position: 'relative',
							}}
						>
							<div
								style={{
									display: 'flex',
									flexDirection: 'column',
									gap: 0,
									alignItems: 'flex-start',
									justifyContent: 'center',
									textAlign: 'left',
									minWidth: 0,
									paddingRight: isSmallViewport ? 10 : 14,
									height: '100%',
									gridColumn: 1,
									gridRow: isSmallViewport ? 1 : 'auto',
								}}
							>
									{isEditingTitle ? (
										<input
											ref={titleInputRef}
											type="text"
											value={titleDraft}
											onChange={(event) => setTitleDraft(event.target.value)}
											onBlur={commitTitleEdit}
											onKeyDown={(event) => {
												if (event.key === 'Enter') {
													event.preventDefault();
													commitTitleEdit();
												}
												if (event.key === 'Escape') {
													event.preventDefault();
													cancelTitleEdit();
												}
											}}
											style={{
												width: 'min(100%, 360px)',
												fontSize: '1.02rem',
												fontWeight: 720,
												lineHeight: 1.15,
												letterSpacing: '0.01em',
												color: safeTextPrimary,
												background: hexToRgba(safeBgPrimary, 0.54, safeBgPrimary),
												border: `1px solid ${hexToRgba(safeAccent, 0.34, safeAccent)}`,
												borderRadius: 10,
												padding: '6px 10px',
												outline: 'none',
												boxShadow: `0 0 0 2px ${hexToRgba(safeAccent, 0.14, safeAccent)}`,
											}}
										/>
									) : (
										<button
											type="button"
											onClick={startTitleEdit}
											style={{
												fontSize: '1.02rem',
												fontWeight: 720,
												lineHeight: 1.15,
												letterSpacing: '0.01em',
												color: safeTextPrimary,
												whiteSpace: 'nowrap',
												overflow: 'hidden',
												textOverflow: 'ellipsis',
												background: 'transparent',
												border: 'none',
												padding: 0,
												cursor: 'text',
												textAlign: 'left',
												maxWidth: '100%',
												transition: 'color 0.2s ease, transform 0.2s ease, opacity 0.2s ease',
											}}
											onMouseEnter={(event) => {
												event.currentTarget.style.color = '#F8FAFC';
												event.currentTarget.style.transform = 'translateY(-1px)';
												event.currentTarget.style.opacity = '0.96';
											}}
											onMouseLeave={(event) => {
												event.currentTarget.style.color = safeTextPrimary;
												event.currentTarget.style.transform = 'translateY(0)';
												event.currentTarget.style.opacity = '1';
											}}
											title="Cliquer pour modifier le titre"
										>
											{displayedNoteName}
										</button>
									)}
							</div>

							<div
								style={{
									position: 'relative',
									display: 'inline-flex',
									alignItems: 'center',
									padding: 0,
									borderRadius: 999,
									border: `1px solid ${hexToRgba(safeTextSecondary, 0.12, safeTextSecondary)}`,
									background: hexToRgba(safeTextPrimary, 0.06, safeTextPrimary),
									justifySelf: 'center',
									height: switchHeight,
									gridColumn: isSmallViewport ? 1 : 'auto',
									gridRow: isSmallViewport ? 1 : 'auto',
									overflow: 'hidden',
									boxShadow: `inset 0 1px 0 ${hexToRgba('#FFFFFF', 0.03, '#FFFFFF')}, 0 4px 10px ${hexToRgba(safeBgPrimary, 0.06, safeBgPrimary)}`,
								}}
							>
								<div
									aria-hidden="true"
									style={{
										position: 'absolute',
										left: 0,
										top: 0,
										bottom: 0,
										width: switchWidth,
										borderRadius: 999,
										background: signInGradient,
										backgroundSize: '300% 300%',
										transform: selectedMode === 'summarized' ? `translateX(${switchTranslateX}px) scale(1.01)` : 'translateX(0px) scale(1)',
										transition: 'transform 420ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 420ms cubic-bezier(0.22, 1, 0.36, 1), filter 240ms ease',
										boxShadow: `0 8px 18px ${hexToRgba(safeAccent, 0.16, safeAccent)}`,
										filter: 'saturate(1.02)',
										pointerEvents: 'none',
									}}
									/>

								<button
									type="button"
									onClick={() => handleSelectAdaptMode('deep')}
									style={{
										position: 'relative',
										zIndex: 1,
										width: switchWidth,
										height: switchHeight,
										padding: 0,
										border: 'none',
										borderRadius: 999,
										fontSize: 14,
										fontWeight: 500,
										letterSpacing: 0,
										cursor: 'pointer',
										textTransform: 'none',
										color: selectedMode === 'deep' ? '#F8FAFC' : hexToRgba(safeTextSecondary, 0.9, safeTextSecondary),
										background: 'transparent',
										boxShadow: 'none',
										transition: 'color 0.24s ease, opacity 0.24s ease, transform 0.24s ease',
									}}
								>
									<span style={{ ...sideMenuLabelTextStyle, whiteSpace: isSmallViewport ? 'normal' : sideMenuLabelTextStyle.whiteSpace }}>{adaptedViewText.deepMode}</span>
								</button>
								<button
									type="button"
									onClick={() => handleSelectAdaptMode('summarized')}
									style={{
										position: 'relative',
										zIndex: 1,
										width: switchWidth,
										height: switchHeight,
										padding: 0,
										border: 'none',
										borderRadius: 999,
										fontSize: 14,
										fontWeight: 500,
										letterSpacing: 0,
										cursor: 'pointer',
										textTransform: 'none',
										color: selectedMode === 'summarized' ? '#F8FAFC' : hexToRgba(safeTextSecondary, 0.9, safeTextSecondary),
										background: 'transparent',
										boxShadow: 'none',
										transition: 'color 0.24s ease, opacity 0.24s ease, transform 0.24s ease',
									}}
								>
									<span style={{ ...sideMenuLabelTextStyle, whiteSpace: isSmallViewport ? 'normal' : sideMenuLabelTextStyle.whiteSpace }}>{adaptedViewText.summarizedMode}</span>
								</button>
							</div>

								{isCurrentFormatGenerated ? (
								<button
									type="button"
									onClick={handleRegenerateCurrentFormat}
									disabled={!hasEditorContent || isAdaptLoading}
									style={{
										padding: actionButtonPadding,
										borderRadius: 12,
										border: 'none',
										fontSize: 13,
										fontWeight: 500,
										letterSpacing: 0,
										cursor: !hasEditorContent || isAdaptLoading ? 'not-allowed' : 'pointer',
										color: !hasEditorContent || isAdaptLoading
											? hexToRgba(safeTextSecondary, 0.72, safeTextSecondary)
											: '#F8FAFC',
										background: !hasEditorContent || isAdaptLoading
											? hexToRgba(safeTextSecondary, 0.18, safeTextSecondary)
											: signInGradient,
										backgroundSize: !hasEditorContent || isAdaptLoading ? 'auto' : '300% 300%',
										boxShadow: !hasEditorContent || isAdaptLoading ? 'none' : `0 12px 28px ${hexToRgba(safeAccent, 0.22, safeAccent)}`,
										transition: 'transform 0.2s ease, filter 0.2s ease, box-shadow 0.2s ease',
										whiteSpace: isSmallViewport ? 'normal' : 'nowrap',
										justifySelf: isSmallViewport ? 'center' : 'end',
										gridColumn: isSmallViewport ? 1 : 'auto',
										gridRow: isSmallViewport ? 2 : 'auto',
										marginTop: isSmallViewport ? 8 : 0,
									}}
									onMouseEnter={(event) => {
										if (!hasEditorContent || isAdaptLoading) return;
										event.currentTarget.style.transform = 'translateY(-1px)';
										event.currentTarget.style.filter = signInHoverButtonStyle.filter;
										event.currentTarget.style.boxShadow = signInHoverButtonStyle.boxShadow;
									}}
									onMouseLeave={(event) => {
										if (!hasEditorContent || isAdaptLoading) return;
										event.currentTarget.style.transform = 'translateY(0)';
										event.currentTarget.style.filter = 'none';
										event.currentTarget.style.boxShadow = signInActiveButtonStyle.boxShadow;
									}}
								>
									<span style={sideMenuLabelTextStyle}>{adaptedViewText.regenerateButton}</span>
								</button>
							) : (
								<div style={{ justifySelf: 'end', width: 0, height: 0 }} />
							)}
						</header>
					</div>

					{adaptError && isCurrentFormatGenerated && (
						<div
							style={{
								margin: headerErrorMargin,
								padding: headerErrorPadding,
								borderRadius: 12,
								border: '1px solid rgba(239,68,68,0.28)',
								background: 'rgba(239,68,68,0.08)',
								display: 'flex',
								alignItems: 'center',
								gap: 10,
							}}
						>
							<span style={{ fontSize: 16, flexShrink: 0 }}>⚠️</span>
							<span style={{ flex: 1, fontSize: '0.88rem', color: safeTextSecondary, lineHeight: 1.45 }}>
								{adaptError}
							</span>
							<button
								type="button"
								onClick={() => setAdaptError(null)}
								style={{ background: 'none', border: 'none', color: safeTextSecondary, cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '0 2px', flexShrink: 0 }}
								aria-label={adaptedViewText.dismissErrorLabel}
							>
								×
							</button>
						</div>
					)}

					<div
						className="note-adapted-scroll"
						style={{
							flex: 1,
							overflowY: 'auto',
							padding: contentPadding,
						}}
					>
						{!isCurrentFormatGenerated ? (
							<div
								style={{
									minHeight: isShortViewport ? 'calc(100% - 140px)' : 'calc(100% - 170px)',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									animation: 'noteAdaptFadeUp 420ms cubic-bezier(0.22, 1, 0.36, 1) both',
								}}
							>
								<div
									style={{
										width: emptyCardWidth,
										padding: emptyCardPadding,
										borderRadius: isSmallViewport ? 18 : 20,
										border: `1px solid ${hexToRgba(safeTextSecondary, 0.18, safeTextSecondary)}`,
										background: `linear-gradient(160deg, ${hexToRgba(safeBgSecondary, 0.95, safeBgSecondary)} 0%, ${hexToRgba(safeBgPrimary, 0.95, safeBgPrimary)} 100%)`,
										boxShadow: 'none',
										textAlign: 'center',
										animation: 'noteAdaptFadeUp 560ms cubic-bezier(0.22, 1, 0.36, 1) both',
									}}
								>
									<div
										style={{
											fontSize: emptyCardTitleSize,
											fontWeight: 720,
											color: safeTextPrimary,
											marginBottom: 10,
										}}
									>
										{adaptedViewText.emptyTitle}
									</div>
									<div
										style={{
											fontSize: emptyCardTextSize,
											lineHeight: 1.5,
											color: safeTextSecondary,
											marginBottom: adaptError ? 12 : 18,
										}}
									>
										{hasEditorContent
											? adaptedViewText.emptyDescriptionWithContent
											: adaptedViewText.noContentFallback}
									</div>
									{adaptError && (
										<div
											style={{
												marginBottom: 16,
												padding: '10px 14px',
												borderRadius: 10,
												border: '1px solid rgba(239,68,68,0.28)',
												background: 'rgba(239,68,68,0.08)',
												fontSize: '0.86rem',
												color: safeTextSecondary,
												lineHeight: 1.5,
												textAlign: 'left',
											}}
										>
											⚠️ {adaptError}
										</div>
									)}
									<button
										type="button"
										onClick={handleGenerateCurrentFormat}
										disabled={!hasEditorContent || isAdaptLoading}
										style={{
											padding: actionButtonPadding,
											borderRadius: 12,
											border: 'none',
											fontSize: actionButtonFontSize,
											fontWeight: 700,
											cursor: !hasEditorContent || isAdaptLoading ? 'not-allowed' : 'pointer',
											color: '#F8FAFC',
											background: !hasEditorContent || isAdaptLoading
												? hexToRgba(safeTextSecondary, 0.26, safeTextSecondary)
												: signInGradient,
											backgroundSize: !hasEditorContent || isAdaptLoading ? 'auto' : '300% 300%',
											boxShadow: !hasEditorContent || isAdaptLoading ? 'none' : `0 12px 28px ${hexToRgba(safeAccent, 0.22, safeAccent)}`,
											transition: 'transform 0.2s ease, filter 0.2s ease, box-shadow 0.2s ease',
										}}
										onMouseEnter={(event) => {
											if (!hasEditorContent || isAdaptLoading) return;
											event.currentTarget.style.transform = 'translateY(-2px) scale(1.01)';
											event.currentTarget.style.filter = signInHoverButtonStyle.filter;
											event.currentTarget.style.boxShadow = signInHoverButtonStyle.boxShadow;
										}}
										onMouseLeave={(event) => {
											if (!hasEditorContent || isAdaptLoading) return;
											event.currentTarget.style.transform = 'translateY(0) scale(1)';
											event.currentTarget.style.filter = 'none';
											event.currentTarget.style.boxShadow = signInActiveButtonStyle.boxShadow;
										}}
									>
										<span style={sideMenuLabelTextStyle}>{adaptedViewText.generateButton}</span>
									</button>
								</div>
							</div>
						) : isKinestheticMode ? (
							<KinesthicView flashcardJsonList={selectedFlashcards} />
						) : normalizedNoteType === 'read' ? (
							<ReadView
								listContent={selectedSmartTextContents}
								listTitles={selectedSmartTextTitles}
							/>
						) : isAudioMode ? (
							<AudioNote
								titles={selectedAudioTitles}
								audioBlocksJson={selectedAudioBlocks}
								className="visual-note-layout"
								titleClassName="visual-note-title"
								sectionClassName="visual-note-section"
							/>
						) : (
							<VisualNote
								titles={selectedVisualTitles}
								mindMapsJson={selectedVisualMaps}
								className="visual-note-layout"
								titleClassName="visual-note-title"
								sectionClassName="visual-note-section"
							/>
						)}
					</div>
				</>
			)}
			{isChatVisible ? (
			<div
				style={{
					position: 'fixed',
					top: 14,
					right: 14,
					bottom: 14,
					left: `${chatPanelLeft}px`,
					width: `${chatPanelRenderedWidth}px`,
					maxWidth: 'calc(100vw - 28px)',
				zIndex: 10001,
					display: 'flex',
					flexDirection: 'column',
					gap: 8,
					opacity: isChatAnimatedIn ? 1 : 0,
					transform: isChatAnimatedIn ? 'translateX(0) scale(1)' : 'translateX(24px) scale(0.985)',
					filter: isChatAnimatedIn ? 'blur(0px)' : 'blur(2px)',
					pointerEvents: isChatAnimatedIn ? 'auto' : 'none',
					transition: 'opacity 260ms ease, transform 320ms cubic-bezier(0.22, 1, 0.36, 1), filter 320ms cubic-bezier(0.22, 1, 0.36, 1), width 320ms cubic-bezier(0.22, 1, 0.36, 1), left 320ms cubic-bezier(0.22, 1, 0.36, 1)',
				}}
			>
				{!isChatFullscreen ? (
					<div
						onMouseDown={handleChatResizeStart}
						role="separator"
						aria-label="Resize chat panel"
						style={{
							position: 'absolute',
							left: -6,
							top: 0,
							bottom: 0,
							width: 12,
							cursor: 'col-resize',
							zIndex: 2,
						}}
					/>
				) : null}
				<div style={{ flex: 1, minHeight: 0 }}>
					<AIChat
						chatConversationJson={chat_conversation_json}
						onSubmit={handleChatSubmit}
						isSubmitting={isChatSubmitting}
						title={t('notes.assistantTitle')}
						placeholder={t('notes.askQuestion')}
						isFullscreen={isChatFullscreen}
						onToggleFullscreen={() => setIsChatFullscreen((previousValue) => !previousValue)}
						onClose={() => setIsChatOpen(false)}
					/>
				</div>
			</div>
			) : null}
			<AIChatFloatingButton
				title={t('notes.openAssistant')}
				ariaLabel={t('notes.openAssistant')}
				onClick={() => setIsChatOpen((previousValue) => !previousValue)}
				style={{
					opacity: isChatVisible ? 0 : 1,
					pointerEvents: isChatVisible ? 'none' : 'auto',
					transform: isChatVisible ? 'translateY(-50%) translateX(24px) scale(0.96)' : 'translateY(-50%) translateX(0) scale(1)',
					transition: 'opacity 260ms ease, transform 320ms cubic-bezier(0.22, 1, 0.36, 1)',
				}}
			/>
		</div>
	);
}

export default NoteAdaptedView;
